import { Box, Card, CardContent, Switch, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";

import { useCan } from "../../../auth/permissions.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { marketingApi } from "../client.js";
import { SectionCardHeader } from "../shared.js";
import type { AgentSettings } from "../types.js";
import { MarketingSteerButton } from "../components/MarketingSteerButton.js";

const SETTINGS_KEY = ["marketing", "agent-settings"] as const;

/**
 * The master "autonomous drafting" switch. Default OFF: the agent only
 * SUGGESTS ideas (see the Ideas tab) and the operator drives generation via
 * Create New / "generate this idea". Flip it ON to let the agent draft from
 * top trends on its own slow cadence (still review-gated). Fully-autonomous
 * mode overrides this. This is the "stop randomly spamming new documents"
 * control (2026-06-15).
 */
export function AutonomousDraftingPanel(): JSX.Element {
  const qc = useQueryClient();
  // Consistent with its sibling Settings panels: the CONTROL is manage-tier
  // ("Manage Marketing settings"), even though reaching the tab is view-tier.
  const canEdit = useCan("marketing:manage");
  const settings = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: marketingApi.getAgentSettings,
    retry: 0,
  });

  const setEnabled = useMutation({
    mutationFn: (auto_draft_enabled: boolean) =>
      marketingApi.patchAgentSettings({ auto_draft_enabled }),
    onSuccess: (next: AgentSettings) => {
      qc.setQueryData(SETTINGS_KEY, next);
      toast({
        title: next.auto_draft_enabled ? "Autonomous drafting on" : "Autonomous drafting off",
      });
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't update",
        description: e instanceof ApiError ? e.message : "Please retry.",
      }),
  });

  const enabled = !!settings.data?.auto_draft_enabled;
  const fullyAuto = settings.data?.autonomy === "fully_auto";
  // fully_auto implies drafting regardless of this toggle.
  const effectiveOn = enabled || fullyAuto;

  return (
    <Card>
      <SectionCardHeader
        title="Autonomous drafting"
        subtitle="Whether the agent drafts content on its own, or only suggests ideas for you to act on."
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                {effectiveOn ? "Auto-drafting is ON" : "Suggest ideas only"}
              </Typography>
              <Box
                component="span"
                data-testid="auto-draft-state"
                sx={[
                  { borderRadius: 999, px: 1, py: 0.25, fontSize: 11, fontWeight: 500 },
                  effectiveOn
                    ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.16), color: "warning.main" }
                    : { bgcolor: "action.hover", color: "text.secondary" },
                ]}
              >
                {effectiveOn ? "drafting" : "ideas only"}
              </Box>
            </Box>
            <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.secondary" }}>
              {fullyAuto
                ? "Fully-autonomous mode is on, so the agent drafts from trends regardless of this switch."
                : enabled
                  ? "The agent drafts from top trends on a slow cadence (every few hours), still gated for your review."
                  : "The agent won't create drafts by itself — it keeps the Ideas tab fresh and you pick what to generate."}
            </Typography>
          </Box>
          <Switch
            checked={effectiveOn}
            disabled={!canEdit || fullyAuto || setEnabled.isPending}
            onChange={(_, checked) => setEnabled.mutate(checked)}
            inputProps={{ "aria-label": "Autonomous drafting" }}
          />
        </Box>

        {/* Steer the agent — moved here from the section header (2026-06-16).
            A short note folded into every agent prompt until cleared. */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
              Steer the agent
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.secondary" }}>
              Drop a short note — it's folded into every agent prompt until you
              clear it (e.g. "Lean into Avaya migration", "No pricing claims this
              week").
            </Typography>
          </Box>
          <MarketingSteerButton />
        </Box>
      </CardContent>
    </Card>
  );
}
